import React, { Fragment, useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Button } from "@mui/material";
import { FiChevronLeft, FiCodesandbox } from "react-icons/fi";

import Scaler from "../../dash/components/scaler/Scaler";
import DashPage from "../../dash/DashPage";

import styles from "./styles";
import flex from "../../../styles/flex";

import { api, IReportQuery, Report, ReportSection, ReportType } from "../../../logic/api";

import { SessionContext } from "../../../utility/useSession";
import usePaths from "../../../utility/paths";
import { isSysAdmin } from "../../../utility/utils";
import AutoRefresh from "../../../utility/autoRefresh";
import { useDate } from '../../../utility/useDate';

import NotFoundPage from "../../error/NotFoundPage";
import { ProgressView } from "../../../components/notifications/progress/ProgressView";
import ReportViewTable from "./components/ReportViewTable";
import ReportGenerator from "./components/ReportGenerator";
import ReportParamInput from "./components/ReportParamInput";
import { ReleaseFlag, useReleaseManagement } from '../../../utility/feature-management';
import ReportRunViewTable from './components/ReportRunViewTable';
import { useTranslation } from 'react-i18next';

interface IReportViewPageParams {
    reportId: string;
}

const ReportView = () => {
    const paths = usePaths();
    const { localizeDate } = useDate();
    const { t } = useTranslation();
    const pageParams: IReportViewPageParams = useParams();
    const executeReportByIdReleased = useReleaseManagement(ReleaseFlag.reportExecuteById);

    const reportId = isNaN(parseInt(pageParams.reportId)) ? -1 : parseInt(pageParams.reportId);

    const session = useContext(SessionContext);
    const history = useHistory();
    const [paramValues, setParamValues] = useState<Record<string, unknown> | null>(null);
    const [showReport, setShowReport] = useState(false);

    const fetchReport = api.hooks.reports.getReport();
    const [reportWithParameters, setReportWithParameters] = useState<IReportQuery | null>(null);
    const headerQuery = ({ ...reportWithParameters } as Report).headerQuery;
    const footerQuery = ({ ...reportWithParameters } as Report).footerQuery;

    const reportHeaderPreviewParams = headerQuery != null && headerQuery.length > 0 ? { ...reportWithParameters, query: headerQuery } : null;
    const reportFooterPreviewParams = footerQuery != null && footerQuery.length > 0 ? { ...reportWithParameters, query: footerQuery } : null;



    useEffect(() => {
        if (fetchReport.data != null && paramValues != null) {
            //console.log("param values changed");
            setReportWithParameters({ ...fetchReport.data, values: paramValues });
        }
    }, [fetchReport.data, paramValues, session.current.site]);

    const viewReports = () => {
        history.goBack();
    }
    const editReport = () => {
        history.push(paths.reportEdit(reportId.toString()));
    }


    const carParkId = !session.current.site?.home?.id || null;
    const [currentCarParkId, setCurrentCarParkId] = useState(carParkId);

    useEffect(() => {
        fetchReport.send(session.current.context, {
            reportId: reportId,
            values: {},
            timestamp: localizeDate(new Date()).toISO() ?? ""
        });
    }, [reportId, carParkId]);

    useEffect(() => {
        if (carParkId != currentCarParkId) {
            setReportWithParameters({ ...(reportWithParameters ?? {}) as IReportQuery });
            setCurrentCarParkId(carParkId);

        }
    }, [carParkId]);

    if (fetchReport.data != null && fetchReport.data.id == null) {
        return <NotFoundPage wrapWithDash={true} title={t("errors.bookingNotFound.title")} message={t("errors.bookingNotFound.message")} />
    }

    return (
        <DashPage>
            <div style={styles.wrapper}>
                <div style={styles.container}>

                    <ProgressView status={fetchReport.status}>
                        {fetchReport.data != null && (
                            <div style={{ textAlign: "left" }}>
                                <div style={{ textAlign: "left" }}>
                                    <Scaler>
                                        <h1 style={styles.title}>{fetchReport.data != null && (fetchReport.data.name)}</h1>
                                    </Scaler>
                                </div>

                                <div style={{ textAlign: "left", marginBottom: 20 }}>
                                    <div style={flex.flexRows}>
                                        <div style={flex.flexAuto}>

                                            <div style={{ paddingRight: 10, paddingBottom: 5, marginTop: 5 }}>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => {
                                                        viewReports();
                                                    }}><FiChevronLeft style={{ marginRight: 10 }} ></FiChevronLeft>{t("general.back")}</Button>
                                            </div>
                                        </div>
                                        {(isSysAdmin(session) && fetchReport.data.reportType == ReportType.standard) && (
                                            <div style={flex.flexAuto}>
                                                <div style={{ paddingRight: 10, paddingBottom: 5, marginTop: 5 }}>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={() => {
                                                            editReport();
                                                        }}><FiCodesandbox style={{ marginRight: 10 }} ></FiCodesandbox>{t("overview.design")}</Button>
                                                </div>
                                            </div>
                                        )}
                                        {showReport == true && (
                                            <div style={flex.flexAuto}>
                                                <div style={{ paddingRight: 10, paddingBottom: 5, marginTop: 5 }}>
                                                    <ReportGenerator reportId={reportId} reportName={fetchReport.data.name} values={paramValues}></ReportGenerator>
                                                </div>
                                            </div>
                                        )}
                                        {showReport == true && (
                                            <div style={flex.flexAuto}>
                                                <div style={{ paddingRight: 10, paddingBottom: 5, marginTop: 5 }}>
                                                    <AutoRefresh durationSeconds={600} onRefresh={() => {
                                                        setReportWithParameters({ ...reportWithParameters as IReportQuery });
                                                    }} ></AutoRefresh>
                                                </div>
                                            </div>

                                        )}
                                    </div>
                                </div>

                                <ReportParamInput values={paramValues} report={fetchReport.data} onParamsChange={(values, show) => {

                                    setParamValues(values);
                                    setShowReport(show);

                                }}>
                                </ReportParamInput>

                                {paramValues != null && (
                                    <>
                                        {(executeReportByIdReleased) && (
                                            <Fragment>
                                                {(showReport == true && reportHeaderPreviewParams != null) && (
                                                    <div style={{ textAlign: "left", paddingTop: 5 }}>
                                                        <ReportRunViewTable
                                                            report={fetchReport.data}
                                                            section={ReportSection.header}
                                                            values={reportHeaderPreviewParams.values}>
                                                        </ReportRunViewTable>
                                                    </div>
                                                )}

                                                {(showReport == true && reportFooterPreviewParams != null) && (
                                                    <div style={{ textAlign: "left", paddingTop: 5 }}>
                                                        <ReportRunViewTable
                                                            report={fetchReport.data}
                                                            section={ReportSection.footer}
                                                            values={reportFooterPreviewParams.values}>
                                                        </ReportRunViewTable>
                                                    </div>
                                                )}

                                                <div style={{ textAlign: "left", paddingTop: 5 }}>
                                                    {showReport == true && (
                                                        <ReportRunViewTable
                                                            report={fetchReport.data}
                                                            section={ReportSection.query}
                                                            values={reportWithParameters?.values}>
                                                        </ReportRunViewTable>
                                                    )}
                                                </div>
                                            </Fragment>
                                        )}

                                        {(!executeReportByIdReleased) && (
                                            <Fragment>
                                                {(showReport == true && reportHeaderPreviewParams != null) && (
                                                    <div style={{ textAlign: "left", paddingTop: 5 }}>
                                                        <ReportViewTable report={reportHeaderPreviewParams as IReportQuery}></ReportViewTable>
                                                    </div>
                                                )}

                                                {(showReport == true && reportFooterPreviewParams != null) && (
                                                    <div style={{ textAlign: "left", paddingTop: 5 }}>
                                                        <ReportViewTable report={reportFooterPreviewParams as IReportQuery}></ReportViewTable>
                                                    </div>
                                                )}

                                                <div style={{ textAlign: "left", paddingTop: 5 }}>
                                                    {showReport == true && (<ReportViewTable report={reportWithParameters as IReportQuery}></ReportViewTable>)}
                                                </div>
                                            </Fragment>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </ProgressView>
                </div>
            </div>
        </DashPage >
    );
}

export default ReportView;
